/**
 * dashboardData.js — Compute dashboard metrics from existing API data.
 * Derives KPIs, revenue trends, pipeline stats, schedule, and activity feed
 * from invoices, quotes, jobs, and customers.
 */

import { isArchived, isQuoteOpen, isCanonicalActive, isCanonicalSent, isConverted } from '../../utils/quoteStatus';
import { formatCurrency, formatCurrencyCompact } from '../../utils/formatCurrency';
import { isJobBacklog } from '../../utils/jobStatus';
import { parseDate } from '../../utils/formatDate';
import { dealerToday } from '../../utils/dealerDate';

// Re-export for backward compatibility with existing consumers
export { formatCurrency, formatCurrencyCompact };

/**
 * Format a number with thousands separators
 */
export function formatNumber(num) {
  return Number(num || 0).toLocaleString('en-US');
}

/**
 * Compute delta percentage between current and prior values
 */
export function computeDelta(current, prior) {
  if (!prior || prior === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - prior) / prior) * 100);
}

/**
 * Determine if a date falls within a range
 */
function inRange(dateStr, start, end) {
  if (!dateStr) return false;
  // IDI-518: paidDate is a bare "Paid on" YYYY-MM-DD; parseDate reads it as
  // that local calendar day (new Date(bare) is UTC midnight — IDI-337).
  const d = parseDate(dateStr);
  if (!d) return false;
  return d >= start && d <= end;
}

/**
 * Compute KPI metrics for the dashboard
 */
export function computeKPIs(invoices = [], quotes = [], jobs = [], period = 'last_6_months') {
  // PR-41 (2026-08-18): the three tiles are BACKLOGS (§3t-ii). Prior-window
  // machinery has been removed — point-in-time deltas from a live population
  // are lies that only look like signal. IDI-68 will supply a stored
  // month-over-month snapshot; until then, `delta` is deliberately absent
  // from each tile and KPIRow renders nothing where the delta used to sit.
  // The guard in KPIRow is PERMANENT: every new tenant lives in the
  // delta-absent state for a month regardless of IDI-68's timing.

  // ——— Jobs Not Scheduled: a stock ———
  // Pack DASH-1: unscheduled backlog. Not period-scoped. A job created nine
  // months ago and still not_scheduled is still backlog today.
  //
  // PR-44 (2026-08-18): the predicate lives in utils/jobStatus.js as
  // `isJobBacklog` — the SAME function DashboardJobsTable imports for
  // its activeJobs filter. `tile === table` now holds by construction.
  const notScheduledJobs = jobs.filter(isJobBacklog);

  // ——— Open Quotes: a stock, dimension-aware ———
  // A1 (2026-08-18): open = active · draft · sent · viewed · won,
  // MINUS converted · expired · lost · archived. `won` STAYS OPEN — a
  // won-but-not-yet-converted quote is accepted work that hasn't become
  // a job. isConverted drops it the moment it does.
  //
  // Archived and converted are DIMENSIONS carried on their own fields
  // (archivedAt, convertedJobId), NOT values of `status`. The Go backend
  // archiveQuote (cmd/quotes/main.go:882) sets archivedAt and never
  // touches status — an exclusion by status alone would count every
  // UI-archived quote as open forever. utils/quoteStatus.isQuoteOpen
  // enforces the correct predicate.
  const openQuotes = quotes.filter(isQuoteOpen);
  const openQuotesTotal = openQuotes.reduce(
    (sum, q) => sum + (Number(q.total_amount) || Number(q.totalAmount) || Number(q.amount) || Number(q.total) || 0),
    0
  );

  // Sub-count for the tile's `sub` line. Applied through isQuoteOpen so
  // an archived-but-sent quote does NOT count as awaiting response.
  const awaitingResponse = quotes.filter(q =>
    isQuoteOpen(q) && ['sent', 'viewed'].includes((q.status || '').toLowerCase())
  ).length;

  // ——— Overdue Invoices: a stock, void excluded ———
  // 331_ §2: `void` is an invoice terminal state — invoiceLifecycle already
  // excludes it from open/overdue tallies elsewhere; the dashboard tile
  // must agree.
  const today = new Date();
  const overdueInvs = invoices.filter(inv => {
    const status = (inv.status || '').toLowerCase();
    if (status === 'paid' || status === 'void') return false;
    const dueDate = inv.due_date || inv.dueDate;
    if (!dueDate) return false;
    return new Date(dueDate) < today;
  });
  const overdueTotal = overdueInvs.reduce(
    (sum, inv) => sum + (Number(inv.total_amount) || Number(inv.totalAmount) || Number(inv.total) || Number(inv.amount) || 0),
    0
  );

  // `period` is unused for KPI computation now. Retained on the signature
  // for API stability — the dashboard passes it in from the PeriodSelector
  // and removing the parameter would ripple to every call site. When IDI-68
  // lands, the stored-snapshot delta consumer will use `period` here again.
  //
  // Correction (PR-44 §4b): a previous comment claimed computeRevenueTrend()
  // still consumed `period`. It does not. `computeRevenueTrend(invoices = [])`
  // takes no period argument; the last-six-months window is hardcoded.
  void period; // silence unused-param lint if a stricter rule ever lands
  return [
    // Pack DASH-1 canonical KPI set: exactly 3 cards.
    // Not Scheduled · Open Quotes · Overdue Invoices
    // (removed: Active Jobs, Revenue MTD per DELIVERY-NOTE iter-18)
    {
      key: 'jobs_not_scheduled',
      label: 'JOBS NOT SCHEDULED',
      value: formatNumber(notScheduledJobs.length),
      rawValue: notScheduledJobs.length,
      // delta: intentionally absent. KPIRow renders nothing when delta is
      // null/undefined (permanent guard). IDI-68 will supply values here.
      invertDelta: true, // rising unscheduled backlog is bad — kept for when delta lands
      sub: `${notScheduledJobs.length === 1 ? 'job' : 'jobs'} awaiting WO assignment`,
      href: '/jobs?status=not_scheduled',
      reportId: 'job_performance',
    },
    {
      key: 'open_quotes',
      label: 'OPEN QUOTES',
      value: formatCurrency(openQuotesTotal),
      rawValue: openQuotesTotal,
      // delta: intentionally absent. See jobs_not_scheduled.
      sub: `${awaitingResponse} awaiting response`,
      href: '/quotes?chip=active',
      reportId: 'quote_performance',
    },
    {
      key: 'overdue',
      label: 'OVERDUE INVOICES',
      value: formatCurrency(overdueTotal),
      rawValue: overdueTotal,
      // delta: intentionally absent.
      invertDelta: true,
      sub: `${overdueInvs.length} invoices past due`,
      // Iter-18: no dedicated /invoices route (R17-10 — invoices are per-job).
      // Overdue Invoices routes to Reports Overview which surfaces past-due as a canonical KPI (RPT-1).
      href: '/reports',
      reportId: 'revenue',
    },
  ];
}

/**
 * Compute revenue trend data (last 6 months)
 */
export function computeRevenueTrend(invoices = []) {
  const now = new Date();
  const months = [];

  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const label = d.toLocaleDateString('en-US', { month: 'short' });

    const monthInvoices = invoices.filter(inv => {
      const status = inv.status?.toLowerCase();
      if (status !== 'paid') return false;
      const date = inv.paid_date || inv.paidDate || inv.updated_at || inv.updatedAt || inv.created_at || inv.createdAt;
      return inRange(date, d, monthEnd);
    });

    const revenue = monthInvoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || Number(inv.totalAmount) || Number(inv.total) || Number(inv.amount) || 0), 0);
    months.push({ month: label, revenue });
  }

  return months;
}

/**
 * Compute prior-period revenue trend (6 months before the current 6-month window)
 * Aligned to the same month labels for chart overlay
 */
export function computePriorRevenueTrend(invoices = []) {
  const now = new Date();
  const months = [];

  for (let i = 5; i >= 0; i--) {
    // Prior period = 6 months before each current month
    const d = new Date(now.getFullYear(), now.getMonth() - i - 6, 1);
    const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    // Use the same label as the current period for alignment
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = currentMonth.toLocaleDateString('en-US', { month: 'short' });

    const monthInvoices = invoices.filter(inv => {
      const status = inv.status?.toLowerCase();
      if (status !== 'paid') return false;
      const date = inv.paid_date || inv.paidDate || inv.updated_at || inv.updatedAt || inv.created_at || inv.createdAt;
      return inRange(date, d, monthEnd);
    });

    const revenue = monthInvoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || Number(inv.totalAmount) || Number(inv.total) || Number(inv.amount) || 0), 0);
    months.push({ month: label, revenue });
  }

  return months;
}

/**
 * Compute quotes pipeline data for donut chart
 */
export function computeQuotesPipeline(quotes = []) {
  const statusLower = (q) => (q.status || '').toLowerCase();

  // R-01 (2026-08-18, ruling §3x): TOTAL counts EVERY quote in the tenant's
  // book. Previously total = active + sent + converted — a sum of the three
  // visible donut segments — which read "TOTAL QUOTES 2378" on a 2,456-
  // quote tenant. 78 rows were in neither the chart nor the total.
  //
  // Segments now cover the whole book, so the donut and the total describe
  // the same set. Archived is a DIMENSION (carried on archivedAt), not a
  // status value; extracted first so a won-but-archived quote counts once,
  // as archived, not twice.
  const archived = quotes.filter(isArchived);
  const unarchived = quotes.filter((q) => !isArchived(q));

  // IDI-146 (2026-09-08): route through the canonical predicates in
  // utils/quoteStatus.js. Pre-extraction: `statusLower(q) === 'active'`
  // matched case-insensitively but ignored the isSent dimension (a
  // status='active' quote with signatureSentAt would count as Active
  // AND separately as Sent — double-counting on the isSent axis).
  // Neither symptom reproduces on tenants 270 or Sample Dealer today
  // (both carry zero isSent, zero drafts, zero uppercase). The
  // divergence class closes here; visible counts unchanged on both.
  //
  // IDI-533 (2026-10-19): "Converted" means a job exists. convertToJob sets
  // status='won' AND convertedJobId, so status alone cannot separate a
  // signed quote from a converted one. Route through the canonical
  // isConverted (convertedJobId, or legacy status 'converted'; never lost)
  // and peel converted rows off FIRST so every other bucket is disjoint
  // from it.
  // `notArchived` below is the not-archived, NOT-converted remainder (the
  // name is kept for the IDI-146 conformance grep).
  const notArchived = unarchived.filter((q) => !isConverted(q));
  const converted = unarchived.length - notArchived.length;
  const active = notArchived.filter(isCanonicalActive).length;
  const sent = notArchived.filter(isCanonicalSent).length;
  // IDI-533: signed (won/accepted) but no job yet → "Accepted". Replaces
  // PR-48's "Won" segment; `accepted` no longer rolls up under Converted
  // (it only did so on the historical assumption that accepted = job).
  const ACCEPTED_STATES = new Set(['won', 'accepted']);
  const accepted = notArchived.filter(q => ACCEPTED_STATES.has(statusLower(q))).length;
  const lost = notArchived.filter(q => statusLower(q) === 'lost').length;
  const expired = notArchived.filter(q => statusLower(q) === 'expired').length;

  // §2 (2026-08-18, 342_ §2): the previous `Math.max(0, ...)` clamp silently
  // hid the exact invariant PR-46's tests are supposed to protect. If future
  // buckets ever overlap, knownSum > quotes.length and `other` would go
  // negative; the clamp muted that to 0, so segments could sum to MORE than
  // total without any test catching it.
  //
  // Now: still clamp (a negative segment would produce a NaN or a broken
  // chart), but LOG so a developer running the app sees the invariant
  // violation immediately. Categories are disjoint TODAY — archived
  // extracted first, status comparisons mutually exclusive — so this log
  // should never fire. If it does, someone added a bucket that overlaps
  // another and the "one row, one segment" invariant is broken.
  const knownSum = active + sent + accepted + converted + lost + expired + archived.length;
  const otherRaw = quotes.length - knownSum;
  if (otherRaw < 0 && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.error(
      `[computeQuotesPipeline] Segment invariant violated: knownSum=${knownSum} > total=${quotes.length}. ` +
      `Buckets must be disjoint. Values: active=${active}, sent=${sent}, accepted=${accepted}, converted=${converted}, ` +
      `lost=${lost}, expired=${expired}, archived=${archived.length}.`
    );
  }
  // Anything with a status we don't recognise (draft / viewed / typo / null)
  // falls into "Other" so the segments always sum to total.
  const other = Math.max(0, otherRaw);

  // Chart palette per R51 / R-02 (§3s): red / navy / neutral / amber. Never
  // green (green is reserved for status pills; using it on a chart would
  // collide with the Active pill's meaning).
  const segments = [];
  if (active > 0) segments.push({ name: 'Active', value: active, fill: 'var(--color-action-primary)' });
  if (sent > 0) segments.push({ name: 'Sent', value: sent, fill: 'var(--color-text-default)' });
  // Accepted (IDI-533; was "Won", PR-48 §1): signed by the customer, no job
  // yet. Palette: --neutral-700 sits between Sent's --color-text-default and
  // Converted's --neutral-500, so "signed, no job yet" (Accepted) reads as
  // distinct from "signed and converted to a job" (Converted).
  if (accepted > 0) segments.push({ name: 'Accepted', value: accepted, fill: 'var(--neutral-700)' });
  if (converted > 0) segments.push({ name: 'Converted', value: converted, fill: 'var(--neutral-500)' });
  if (lost > 0) segments.push({ name: 'Lost', value: lost, fill: 'var(--neutral-400)' });
  if (expired > 0) segments.push({ name: 'Expired', value: expired, fill: 'var(--neutral-300)' });
  if (archived.length > 0) segments.push({ name: 'Archived', value: archived.length, fill: 'var(--neutral-200)' });
  if (other > 0) segments.push({ name: 'Other', value: other, fill: 'var(--neutral-100)' });

  return { segments, total: quotes.length };
}

/**
 * Get today's scheduled jobs
 */
export function getTodaysSchedule(jobs = []) {
  const todayStr = dealerToday(); // IDI-615 D2

  const scheduled = jobs.filter(j => {
    const schedDate = j.scheduled_date || j.scheduledDate || j.start_date || j.startDate;
    if (!schedDate) return false;
    const dateStr = typeof schedDate === 'string' ? schedDate.split('T')[0] : '';
    return dateStr === todayStr;
  });

  const notScheduled = jobs.filter(j => {
    const status = j.status?.toLowerCase();
    return (status === 'pending' || status === 'unscheduled' || status === 'new' || status === 'not_scheduled') &&
      !(j.scheduled_date || j.scheduledDate || j.start_date || j.startDate);
  });

  return { scheduled, notScheduledCount: notScheduled.length };
}

/**
 * Build recent activity feed from all entities
 */
export function buildActivityFeed(invoices = [], quotes = [], jobs = [], limit = 10) {
  const activities = [];

  quotes.forEach(q => {
    const date = q.updated_at || q.updatedAt || q.created_at || q.createdAt;
    if (!date) return;
    const seqId = q.quoteNumber || q.quote_number || q.sequential_id || q.sequentialId || q.id;
    activities.push({
      id: q.id,
      type: 'quote',
      entity: `Quote ${seqId}`,
      action: getQuoteAction(q),
      date: new Date(date),
      link: `/quotes/${q.id}`,
    });
  });

  jobs.forEach(j => {
    const date = j.updated_at || j.updatedAt || j.created_at || j.createdAt;
    if (!date) return;
    const seqId = j.projectNumber || j.project_number || j.sequential_id || j.sequentialId || j.job_number || j.jobNumber || j.id;
    activities.push({
      id: j.id,
      type: 'job',
      entity: `Job ${seqId}`,
      action: getJobAction(j.status),
      date: new Date(date),
      link: `/jobs/${j.id}`,
    });
  });

  invoices.forEach(inv => {
    const date = inv.updated_at || inv.updatedAt || inv.created_at || inv.createdAt;
    if (!date) return;
    const seqId = inv.sequential_id || inv.sequentialId || inv.invoice_number || inv.invoiceNumber || inv.id?.slice(-6);
    activities.push({
      id: inv.id,
      type: 'invoice',
      entity: `Invoice INV-${seqId}`,
      action: getInvoiceAction(inv.status),
      date: new Date(date),
      link: `/invoices/${inv.id}`,
    });
  });

  // Sort by date descending, take top N
  activities.sort((a, b) => b.date - a.date);
  return activities.slice(0, limit);
}

// IDI-533: takes the quote, not the status. "converted" only when a job
// exists (isConverted); a signed quote without a job is "accepted".
function getQuoteAction(q) {
  if (isConverted(q)) return 'converted';
  switch (q?.status?.toLowerCase()) {
    case 'sent': return 'sent';
    case 'active': return 'created';
    case 'accepted':
    case 'won': return 'accepted';
    case 'rejected':
    case 'lost': return 'lost';
    default: return 'updated';
  }
}

function getJobAction(status) {
  switch (status?.toLowerCase()) {
    case 'scheduled': return 'scheduled';
    case 'in_progress': return 'started';
    case 'completed': return 'completed';
    case 'invoiced': return 'invoiced';
    default: return 'updated';
  }
}

function getInvoiceAction(status) {
  switch (status?.toLowerCase()) {
    case 'sent': return 'sent';
    case 'paid': return 'paid';
    case 'overdue': return 'overdue';
    case 'unsent': return 'created';
    default: return 'updated';
  }
}

/**
 * Format relative time (e.g., "2h ago", "3d ago")
 */
export function formatRelativeTime(date) {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
